import React, { useState, useEffect, useCallback, useRef } from 'react';
import { emailSettingsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const emptyForm = {
  provider: '',
  gmailUser: '',
  gmailPw1: '',
  gmailPw2: '',
  gmailPw3: '',
  gmailPw4: '',
  outlookUser: '',
  outlookClientId: '',
  outlookClientSecret: '',
  outlookRefreshToken: '',
  outlookTenantId: '',
  smtpHost: '',
  smtpPort: '587',
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
  testEmail: ''
};

const EmailSystemTab = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [remote, setRemote] = useState(null);
  const [mode, setMode] = useState('idle');
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await emailSettingsAPI.get();
      setRemote(data);
      if (data.hasSettings) {
        setMode('summary');
        setForm(emptyForm);
      } else {
        setMode('empty');
      }
    } catch (e) {
      showError(e.response?.data?.error || 'Ayarlar yüklenemedi');
      setMode('empty');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const fillFormForEdit = (data) => {
    setForm({
      ...emptyForm,
      provider: data.provider || '',
      gmailUser: data.gmailUser || '',
      gmailPw1: '',
      gmailPw2: '',
      gmailPw3: '',
      gmailPw4: '',
      outlookUser: data.outlookUser || '',
      outlookClientId: data.outlookClientId || '',
      outlookClientSecret: '',
      outlookRefreshToken: '',
      outlookTenantId: data.outlookTenantId || '',
      smtpHost: data.smtpHost || '',
      smtpPort: data.smtpPort != null ? String(data.smtpPort) : '587',
      smtpSecure: Boolean(data.smtpSecure),
      smtpUser: data.smtpUser || '',
      smtpPassword: '',
      testEmail: ''
    });
  };

  const startAdd = () => {
    setForm({ ...emptyForm });
    setMode('pick');
  };

  const startEdit = () => {
    if (!remote?.hasSettings) return;
    fillFormForEdit(remote);
    setMode('form');
  };

  const pickProvider = (provider) => {
    setForm((f) => ({ ...emptyForm, provider, testEmail: f.testEmail }));
    setMode('form');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const buildPayload = () => {
    const p = { provider: form.provider, testEmail: form.testEmail.trim() };
    if (form.provider === 'gmail') {
      p.gmailUser = form.gmailUser.trim();
      p.gmailAppPassword =
        form.gmailPw1 + form.gmailPw2 + form.gmailPw3 + form.gmailPw4;
    } else if (form.provider === 'outlook') {
      p.outlookUser = form.outlookUser.trim();
      p.outlookClientId = form.outlookClientId.trim();
      p.outlookClientSecret = form.outlookClientSecret;
      p.outlookRefreshToken = form.outlookRefreshToken;
      p.outlookTenantId = form.outlookTenantId.trim();
    } else if (form.provider === 'smtp') {
      p.smtpHost = form.smtpHost.trim();
      p.smtpPort = form.smtpPort;
      p.smtpSecure = form.smtpSecure;
      p.smtpUser = form.smtpUser.trim();
      p.smtpPassword = form.smtpPassword;
    }
    return p;
  };

  const gmailPwRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const gmailPasswordCombined = () =>
    form.gmailPw1 + form.gmailPw2 + form.gmailPw3 + form.gmailPw4;

  const handleGmailPwChange = (index, e) => {
    const raw = e.target.value;
    const segment = raw.replace(/\s/g, '').slice(0, 4);
    const keys = ['gmailPw1', 'gmailPw2', 'gmailPw3', 'gmailPw4'];
    setForm((f) => ({ ...f, [keys[index]]: segment }));
    if (segment.length === 4 && index < 3) {
      setTimeout(() => gmailPwRefs[index + 1].current?.focus(), 0);
    }
  };

  const handleGmailFirstPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text') || '';
    const clean = text.replace(/\s+/g, '').slice(0, 16);
    const parts = [0, 1, 2, 3].map((i) => clean.slice(i * 4, i * 4 + 4));
    setForm((f) => ({
      ...f,
      gmailPw1: parts[0],
      gmailPw2: parts[1],
      gmailPw3: parts[2],
      gmailPw4: parts[3]
    }));
    if (clean.length > 0) {
      const focusIdx = Math.min(3, Math.floor(clean.length / 4));
      setTimeout(() => gmailPwRefs[focusIdx].current?.focus(), 0);
    }
  };

  const handleGmailPwKeyDown = (index, e) => {
    if (e.key !== 'Backspace') return;
    const keys = ['gmailPw1', 'gmailPw2', 'gmailPw3', 'gmailPw4'];
    const val = form[keys[index]];
    if (!val && index > 0) {
      e.preventDefault();
      gmailPwRefs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.testEmail.trim()) {
      showError('Test e-posta adresi girin');
      return;
    }
    if (form.provider === 'gmail') {
      const combined = gmailPasswordCombined();
      const hasAny = [form.gmailPw1, form.gmailPw2, form.gmailPw3, form.gmailPw4].some(
        (s) => s.length > 0
      );
      if (hasAny && combined.length !== 16) {
        showError('Uygulama şifresi toplam 16 karakter olmalıdır (dört grup, her biri 4 karakter)');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = await emailSettingsAPI.save(payload);
      showSuccess(data.message || 'Kaydedildi');
      await load();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Test başarısız; ayarlar kaydedilmedi';
      showError(typeof msg === 'string' ? msg : 'İşlem başarısız', 8000);
    } finally {
      setSaving(false);
    }
  };

  const cancelForm = () => {
    if (remote?.hasSettings) {
      setMode('summary');
      setForm(emptyForm);
      return;
    }
    if (mode === 'form' && form.provider) {
      setMode('pick');
      setForm(emptyForm);
      return;
    }
    setMode('empty');
    setForm(emptyForm);
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-600">Yükleniyor...</div>
    );
  }

  if (mode === 'summary' && remote?.hasSettings) {
    const { summary } = remote;
    return (
      <div className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Kayıtlı e-posta ayarı
          </h3>
          <p className="text-gray-700">
            <span className="font-medium">Servis:</span> {summary.label}
          </p>
          <p className="text-gray-700 mt-1">
            <span className="font-medium">Adres:</span> {summary.address}
          </p>
          {summary.host != null && (
            <p className="text-gray-600 text-sm mt-1">
              {summary.host}:{summary.port}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={startEdit}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
        >
          Ayarları düzenle
        </button>
      </div>
    );
  }

  if (mode === 'empty') {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <p className="text-gray-600 mb-4">Henüz kayıtlı e-posta gönderim ayarı yok.</p>
        <button
          type="button"
          onClick={startAdd}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
        >
          Yeni kayıt ekle
        </button>
      </div>
    );
  }

  if (mode === 'pick') {
    return (
      <div className="space-y-6">
        <p className="text-gray-700">E-posta servisini seçin:</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { id: 'gmail', title: 'Gmail', desc: 'Uygulama şifresi ile' },
            { id: 'outlook', title: 'Outlook', desc: 'OAuth2' },
            { id: 'smtp', title: 'SMTP', desc: 'Özel sunucu' }
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => pickProvider(opt.id)}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 text-left transition-colors"
            >
              <div className="font-semibold text-gray-900">{opt.title}</div>
              <div className="text-sm text-gray-500 mt-1">{opt.desc}</div>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={cancelForm}
          className="text-gray-600 hover:text-gray-800"
        >
          ← Vazgeç
        </button>
      </div>
    );
  }

  const secretsHint = (key) => {
    if (!remote?.secretsPresent?.[key]) return null;
    return (
      <p className="text-xs text-amber-700 mt-1">
        Kayıtlı; değiştirmek için yeni değer girin (boş bırakırsanız mevcut değer
        kullanılır)
      </p>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {remote?.hasSettings && mode === 'form' && (
        <div className="text-sm text-gray-600">
          Servis: <strong>{form.provider}</strong>
        </div>
      )}

      {form.provider === 'gmail' && (
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-gray-800">Gmail (uygulama şifresi)</h4>
          <p className="text-sm text-gray-600">
            Google Hesabınızda 2 adımlı doğrulama ve uygulama şifresi oluşturun.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gmail adresi
            </label>
            <input
              name="gmailUser"
              value={form.gmailUser}
              onChange={handleChange}
              required
              autoFocus
              autoComplete="off"
              type="email"
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="ornek@gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Uygulama şifresi (16 karakter — dört grup)
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  ref={gmailPwRefs[i]}
                  type="text"
                  inputMode="text"
                  name={`gmailPw${i + 1}`}
                  value={form[`gmailPw${i + 1}`]}
                  onChange={(e) => handleGmailPwChange(i, e)}
                  onKeyDown={(e) => handleGmailPwKeyDown(i, e)}
                  onPaste={i === 0 ? handleGmailFirstPaste : undefined}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={4}
                  required={!remote?.secretsPresent?.gmailAppPassword}
                  className="w-14 sm:w-16 px-2 py-2 border rounded-lg text-center font-mono text-base tracking-widest"
                  placeholder={remote?.secretsPresent?.gmailAppPassword ? '••••' : '••••'}
                  aria-label={`Uygulama şifresi grup ${i + 1}, en fazla 4 karakter`}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Her kutuya en fazla 4 karakter. İlk kutuda boşluklu metin yapıştırırsanız (ör. 4×4
              biçiminde) otomatik bölünür.
            </p>
            {secretsHint('gmailAppPassword')}
          </div>
        </div>
      )}

      {form.provider === 'outlook' && (
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-gray-800">Outlook (OAuth2)</h4>
          <p className="text-sm text-gray-600">
            Azure / Microsoft Entra kayıtlı uygulamanızdan client ID, secret ve
            yenileme jetonunu girin.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Outlook e-posta
            </label>
            <input
              name="outlookUser"
              value={form.outlookUser}
              onChange={handleChange}
              required
              autoFocus
              autoComplete="off"
              type="email"
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Uygulama (client) ID
            </label>
            <input
              name="outlookClientId"
              value={form.outlookClientId}
              onChange={handleChange}
              required
              autoComplete="off"
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client secret
            </label>
            <input
              name="outlookClientSecret"
              value={form.outlookClientSecret}
              onChange={handleChange}
              autoComplete="off"
              type="password"
              className="w-full px-4 py-2 border rounded-lg"
              required={!remote?.secretsPresent?.outlookClientSecret}
            />
            {secretsHint('outlookClientSecret')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OAuth2 yenileme jetonu (refresh token)
            </label>
            <input
              name="outlookRefreshToken"
              value={form.outlookRefreshToken}
              onChange={handleChange}
              autoComplete="off"
              type="password"
              className="w-full px-4 py-2 border rounded-lg"
              required={!remote?.secretsPresent?.outlookRefreshToken}
            />
            {secretsHint('outlookRefreshToken')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kiracı (tenant) ID — isteğe bağlı
            </label>
            <input
              name="outlookTenantId"
              value={form.outlookTenantId}
              onChange={handleChange}
              autoComplete="off"
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="common veya organizations"
            />
          </div>
        </div>
      )}

      {form.provider === 'smtp' && (
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-gray-800">SMTP</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sunucu
              </label>
              <input
                name="smtpHost"
                value={form.smtpHost}
                onChange={handleChange}
                required
                autoFocus
                autoComplete="off"
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="smtp.ornek.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Port
              </label>
              <input
                name="smtpPort"
                value={form.smtpPort}
                onChange={handleChange}
                required
                autoComplete="off"
                type="number"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              name="smtpSecure"
              checked={form.smtpSecure}
              onChange={handleChange}
              type="checkbox"
            />
            <span className="text-sm text-gray-700">TLS/SSL (secure)</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kullanıcı adı
            </label>
            <input
              name="smtpUser"
              value={form.smtpUser}
              onChange={handleChange}
              required
              autoComplete="off"
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Şifre
            </label>
            <input
              name="smtpPassword"
              value={form.smtpPassword}
              onChange={handleChange}
              autoComplete="off"
              type="password"
              className="w-full px-4 py-2 border rounded-lg"
              required={!remote?.secretsPresent?.smtpPassword}
            />
            {secretsHint('smtpPassword')}
          </div>
        </div>
      )}

      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test e-postası gönderilecek adres
        </label>
        <input
          name="testEmail"
          value={form.testEmail}
          onChange={handleChange}
          required
          autoComplete="off"
          type="email"
          className="w-full max-w-md px-4 py-2 border rounded-lg"
          placeholder="test@ornek.com"
        />
        <p className="text-xs text-gray-500 mt-2">
          Test başarılı olursa ayarlar kaydedilir; başarısız olursa mevcut kayıt
          değişmez.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Gönderiliyor...' : 'Test gönder ve kaydet'}
        </button>
        <button
          type="button"
          onClick={cancelForm}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          İptal
        </button>
      </div>
    </form>
  );
};

export default EmailSystemTab;
